import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  HStack,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  InputGroup,
  InputLeftElement,
  Badge,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import TableSkeleton from '../components/TableSkeleton';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

const PropietarioTesoreria: React.FC = () => {
  const [transactions, setTransactions] = useState([]);
  const [paginatedTransactions, setPaginatedTransactions] = useState([]);
  const [selectedPredio, setSelectedPredio] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFetchingTransactions, setFetchingTransactions] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  
  const fetchPadronData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/padron?page=${page}&pageSize=${pageSize}&search=${searchQuery}`,
      );
      const data = await response.json();
      setPaginatedTransactions(data.data);
      setTotalPages(data.pagination.totalPages);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos del padrón.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (rfc: string) => {
    setFetchingTransactions(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Transacciones/rfc/${rfc}`,
      );
      const data = await response.json();
      setTransactions(data);
      setFetchingTransactions(false);
      setModalOpen(true); // Abrir modal después de obtener las transacciones
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las transacciones.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setFetchingTransactions(false);
    }
  };
  const user = useSelector((state: RootState) => state.auth.user);

  const handleRowClick = async (padron: any) => {
    setSelectedPredio(padron);
    if (padron.rfc) {
      await fetchTransactions(padron.rfc); // Consultar transacciones
    } else {
      toast({
        title: 'Advertencia',
        description: 'El padrón seleccionado no tiene un RFC asociado.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchPadronData();
  }, [page, pageSize, searchQuery]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };
  const downloadExcel = useCallback(() => {
    // Formatear los datos para exportación
    const formattedData = paginatedTransactions.map((item: any) => ({
      ID: item.id,
      Nombre: item.nombre_completo,
      RFC: item.rfc,
      Email: item.correo_electronico,
      Teléfono: item.telefono,
      Domicilio: item.domicilio,
      Procedencia: item.lugar_procedencia,
    }));
  
    try {
      // Crear la hoja y el libro
      const ws = XLSX.utils.json_to_sheet(formattedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Padron de Ciudadanos');
  
      // Descargar el archivo
      XLSX.writeFile(wb, 'Padron.xlsx');
    } catch (error) {
      console.error('Error al generar el archivo Excel:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar el archivo Excel.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [paginatedTransactions, toast]);
  
  return (
    <Box p={4} pt={79} width="100%">
      {/* Barra de búsqueda y reportes */}
      <HStack justify="space-between" mb={6}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input 
            placeholder="Buscar ciudadanos..." 
            value={searchQuery} 
            onChange={handleSearch}
            bg="white"
            borderColor="gray.200"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          />
        </InputGroup>
        
        {user.rol == 'A1' && (
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              bg="#bb8c52" 
              color="white" 
              _hover={{ bg: "#a67a47" }}
            >
              Reportes
            </MenuButton>
            <MenuList>
              <MenuItem onClick={downloadExcel}>Descargar XLS</MenuItem>
            </MenuList>
          </Menu>
        )}
      </HStack>

      {/* Tabla moderna */}
      {loading ? (
        <TableSkeleton rows={pageSize} columns={7} hasAvatar={false} />
      ) : (
        <Box 
          bg="white" 
          borderRadius="lg" 
          boxShadow="sm" 
          border="1px solid" 
          borderColor="gray.200"
          overflow="hidden"
          mb={6}
        >
          <Box overflowX="auto">
            <Table variant="simple" size="md">
              <Thead bg="gray.50">
                <Tr>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">ID</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Nombre Completo</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">RFC</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Email</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Teléfono</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Domicilio</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Procedencia</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedTransactions.map((padron: any) => (
                  <Tr
                    key={padron.id}
                    onClick={() => handleRowClick(padron)}
                    cursor="pointer"
                    _hover={{ bg: "#bb8c5210" }}
                    transition="all 0.2s"
                  >
                    <Td>
                      <Badge 
                        bg="#bb8c52" 
                        color="white" 
                        fontSize="xs"
                        borderRadius="full"
                        px={2}
                      >
                        #{padron.id}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontWeight="semibold" color="#681a2d" fontSize="sm">
                        {padron.nombre_completo}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" fontFamily="mono">
                        {padron.rfc || '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {padron.correo_electronico || '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {padron.telefono || '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" noOfLines={2}>
                        {padron.domicilio || '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {padron.lugar_procedencia || '-'}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Paginación */}
      <HStack spacing={4} justify="center">
        <IconButton
          icon={<ChevronLeftIcon />}
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          isDisabled={page === 1}
          aria-label="Página anterior"
          variant="outline"
          borderColor="#bb8c52"
          color="#bb8c52"
          _hover={{ bg: "#bb8c52", color: "white" }}
          size="sm"
        />
        <Text color="#681a2d" fontWeight="medium" fontSize="sm">
          Página {page} de {totalPages}
        </Text>
        <IconButton
          icon={<ChevronRightIcon />}
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          isDisabled={page === totalPages}
          aria-label="Página siguiente"
          variant="outline"
          borderColor="#bb8c52"
          color="#bb8c52"
          _hover={{ bg: "#bb8c52", color: "white" }}
          size="sm"
        />
      </HStack>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Transacciones del RFC: {selectedPredio?.rfc}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isFetchingTransactions ? (
              <Text>Cargando transacciones...</Text>
            ) : transactions.length > 0 ? (
              <Table variant="striped" colorScheme="gray">
                <Thead>
                  <Tr>
                    <Th>ID</Th>
                    <Th>Concepto</Th>
                    <Th>Razón Social</Th>
                    <Th>Método de Pago</Th>
                    <Th>Monto</Th>
                    <Th>Fecha</Th>
                    <Th>Estatus</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {transactions.map((transaction: any) => (
                    <Tr key={transaction.ID}>
                      <Td>{transaction.ID}</Td>
                      <Td>{transaction.Concepto}</Td>
                      <Td>{transaction.RazonSocial}</Td>
                      <Td>{transaction.MetodoPago}</Td>
                      <Td>${transaction.Monto?.toFixed(2)}</Td>
                      <Td>
                        {new Date(transaction.Fecha).toLocaleDateString()}
                      </Td>
                      <Td>
                        {transaction.estatus_pago ? 'Pagado' : 'Pendiente'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text>No se encontraron transacciones para este RFC.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button bg="#5b182b" color="white" _hover={{ bg: "#691f30" }} onClick={() => setModalOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PropietarioTesoreria;
